<script>
import { authState } from "@app/store/authStore.svelte.js";
import {
    environment
} from '@app/store/envStore.svelte.js';
import {
    speakerState
} from '@app/store/speakerStore.svelte.js';
import {
    issueState
} from '@app/store/issueStore.svelte.js';
import {
    markerState
} from '@app/store/markerStore.svelte.js';
import {
    metadataState
} from '@app/store/metadataStore.svelte.js';
import {
    styleState
} from '@app/store/styleStore.svelte.js';
import {
    eventGroupState
} from '@app/store/eventGroupStore.svelte.js';
import {
    projectState
} from '@app/store/projectStore.svelte.js';
import { toast } from '@zerodevx/svelte-toast';
import {
    modalState, hideModal
} from '@app/store/modalStore.svelte.js';
import {
    fade
} from 'svelte/transition';
import {
    v4 as uuidv4
} from 'uuid';
import {
    Circle
} from 'svelte-loading-spinners'
import {
    BarLoader
} from 'svelte-loading-spinners'
import { onMount } from 'svelte'; // Add onMount import
import * as m from '@app/paraglide/messages.js';

/* Firebase */
import firebase from '@app/configs/firebase.js';
import db from '@app/configs/firestore.js';
import storage from '@app/configs/storage.js';

import { ccprjFunc } from '@app/lib/ccprj.js';
import _EventGroup from '@cclib/classes/eventGroup.js';
import insertEvent from '@cclib/functions/eventGroups/insertEvent.js';

let selectedRecord = $state(),
    automaticBackupTimer,
    automaticSaveTimer,
    backupDestination = environment.online ? "Cloud" : "Local",
    targetFolder,
    numberOfBackups = 5,
    backupInterval = 15,
    gettingRecords = $state(),
    loading = $state(false),
    enableAutomaticSave = false,
    automaticSaveInterval = 5,
    records = $state([]),
    backupCount = 1,
    userId = environment.online ? firebase.auth().currentUser.uid : null,
    homeRef = environment.online ? db.collection("users").doc(userId).collection("autosave") : null,
    storageRef = environment.online ? storage.ref() : null,
    snapshotUploadBasePath = environment.online ? "users/" + userId + "/autosave/" : null;

onMount(() => {
    //console.log("Starting Automatic Saving");
    //Get Automatic backup settings from local storage
    let settings = JSON.parse(localStorage.getItem("cc-automatic-backup-settings"));
    if (settings) {
        backupDestination = settings.backupDestination;
        targetFolder = settings.targetFolder;
        numberOfBackups = settings.numberOfBackups > 0 ?  settings.numberOfBackups : 5;
        backupInterval = Math.max(15,settings.backupInterval);
        enableAutomaticSave = settings.enableAutomaticSave;
        automaticSaveInterval = Math.max(5, settings.automaticSaveInterval);

        /* Set intervals for backup and restore */
        automaticBackupTimer = setInterval(autoBackup, settings.backupInterval * 60000);

        if (enableAutomaticSave) {
            automaticSaveTimer = setInterval(autoSave, settings.automaticSaveInterval * 60000);
        }
    } else {
        automaticBackupTimer = setInterval(autoBackup, 900000); //300,000 = 5 Minutes, 900,000 = 15 minutes
    }

    // Cleanup function
    return () => {
        clearInterval(automaticBackupTimer);
        if (automaticSaveTimer) {
            clearInterval(automaticSaveTimer);
        }
    };
});

function selectRecord(record) {
    selectedRecord = record;
    //console.log(record);
}

function refreshRecords() {
    gettingRecords = getRecords();
}

function getRecords() {
    selectedRecord = undefined;
    records = [];
    if (!environment.online) {
        if (environment.iframe){
            return;
        }
        
        toast.push(m.automatic_save_offline_unavailable(), {
            classes: ["toast-warning"]
        });
        return;
    }
    let twoWeeksAgo = new Date();
    twoWeeksAgo.setHours(twoWeeksAgo.getHours() - 336);
    return homeRef.where("createdOn", ">=", twoWeeksAgo).orderBy('createdOn', 'desc').limit(100).get().then((querySnapshot) => {
        querySnapshot.forEach((doc) => {
            records = [...records, doc.data()];
        });
    }).catch((error) => {
        console.log("Auto Save: error getting documents: ", error, error.message);
    });
}

//Save project
async function autoSave(){
    let projectJson = ccprjFunc.createCcprj({
        project : JSON.parse(JSON.stringify(projectState)),
        metadata : JSON.parse(JSON.stringify(metadataState)),
        speakers : JSON.parse(JSON.stringify(speakerState)),
        issues : JSON.parse(JSON.stringify(issueState.value)),
        markers : JSON.parse(JSON.stringify(markerState)),
        eventGroups : JSON.parse(JSON.stringify(eventGroupState)),
        style : JSON.parse(JSON.stringify(styleState))
    });

    if (projectState.localPath && projectState.useLocalStorage && environment.electron){
        try {
            const fs = window.fs;
            await fs.writeFile(projectState.localPath, JSON.stringify(projectJson, null, 4));
            toast.push(m.automatic_save_completed_success(), {
                classes: ["toast-success"]
            });
        } catch(err){
            toast.push(m.automatic_save_failed({ message: err.message }), {
                classes: ["toast-danger"]
            });
        }
    } else if ((projectState.folderId || projectState.teamId) && !projectState.useLocalStorage && environment.online){
        try {
            let projectFilePath, dbRef;
            if (projectState.rootDir === 'team' && projectState.teamId){
                let teamId = authState.team ? authState.team.id : null;
                if (!teamId){
                    return;
                }

                projectFilePath = "teams/" + teamId + "/storage/" + projectState.id;
                dbRef = db.collection("teams").doc(teamId).collection("storage").doc(projectState.id);
            } else if (projectState.rootDir === 'user' && projectState.folderId) {
                if (!userId){
                    return;
                }

                projectFilePath = "users/" + userId + "/projects/" + projectState.id;
                dbRef = db.collection("users").doc(userId).collection("storage").doc(projectState.id);
            } else {
                console.log("---Automatic Save Enabled---");
                console.log("Project is not saved. Please save the project first.");
                return;
            }

            // Check if the project is locked
            const docRef = await dbRef.get();
            const userEmail = firebase.auth().currentUser.email;
            
            if (docRef.exists) {
                const projectRecord = docRef.data();
                
                // If project is locked by someone else, skip auto-save
                if (projectRecord.locked && projectRecord.lockedBy !== userEmail) {
                    console.log(`Automatic save skipped: Project is locked by ${projectRecord.lockedBy}`);
                    toast.push(m.automatic_save_skipped_locked({ email: projectRecord.lockedBy }), {
                        classes: ["toast-warning"]
                    });
                    return;
                }
            }

            let uploadProjectRef = storageRef.child(projectFilePath);
            await uploadProjectRef.putString(JSON.stringify(projectJson));
            await dbRef.update({
                updatedOn: firebase.firestore.Timestamp.fromDate(new Date())
            });
            toast.push(m.automatic_save_completed_success(), {
                classes: ["toast-success"]
            });
        } catch(err){
            toast.push(m.automatic_save_failed({ message: err.message }), {
                classes: ["toast-danger"]
            });
        }
    } else {
        console.log("---Automatic Save Enabled---");
        console.log("Project is not saved. Please save the project first.");
    }
}

//Save snapshot
async function autoBackup() {
    if (backupDestination === "Cloud") {
        autoSaveEventGroups();
    } else {
        autoSaveProject();
    }
}

async function autoSaveProject() {
    try {
        const fs = window.fs;
        const path = window.path;
        if (targetFolder) {
            let projectJson = ccprjFunc.createCcprj({
                project : JSON.parse(JSON.stringify(projectState)),
                metadata : JSON.parse(JSON.stringify(metadataState)),
                speakers : JSON.parse(JSON.stringify(speakerState)),
                issues : JSON.parse(JSON.stringify(issueState.value)),
                markers : JSON.parse(JSON.stringify(markerState)),
                eventGroups : JSON.parse(JSON.stringify(eventGroupState)),
                style : JSON.parse(JSON.stringify(styleState))
            });
            
           
            await fs.writeFile(path.join(targetFolder,projectState.name+"_BACKUP_"+backupCount+".ccprj"), JSON.stringify(projectJson, null, 4));

            backupCount++;
            
            if (backupCount > numberOfBackups){
                backupCount = 1;
            }

            toast.push(m.automatic_save_backup_saved_successfully(), {
                classes: ["toast-success"]
            });
        } else {
            toast.push(m.automatic_save_backup_not_configured(), {
                classes: ["toast-warning"]
            });
        }
    } catch(err){
        toast.push(m.automatic_save_project_save_failed({ message: err.message }), {
            classes: ["toast-danger"]
        });
    }    
}

async function autoSaveEventGroups() {
    selectedRecord = undefined;
    let recordId = uuidv4();
    let totalEventCount = 0;

    //Get the total number of events across all event groups
    totalEventCount = eventGroupState.reduce((acc, eventGroup) => {
        return acc + eventGroup.events.length;
    }, 0);

    if (totalEventCount < 5) {
        return;
    }

    let recordInfo = {
        id: recordId,
        name: projectState.name,
        type: projectState.type === "team" ? "team project" : "project",
        createdOn: firebase.firestore.Timestamp.fromDate(new Date()),
        eventGroups: eventGroupState.length,
        events: totalEventCount
    }

    try {
        await storageRef.child(snapshotUploadBasePath + recordId).putString(JSON.stringify(eventGroupState));
        //console.log("Event Groups saved to storage");
        await homeRef.doc(recordId).set(recordInfo);
        //console.log("Autosave Record Stored");
        toast.push(m.automatic_save_restore_point_created(), {
            classes: ["toast-info"]
        });
    } catch (err) {
        console.log("Error saving project: ", err);
        toast.push(m.automatic_save_restore_point_failed({ message: err.message }), {
            classes: ["toast-danger"]
        });
    }
}

function restoreEventGroups() {
    loading = true;
    storageRef.child(snapshotUploadBasePath + selectedRecord.id).getDownloadURL().then((url) => {
        return fetch(url);
    }).then(res => {
        return res.json();
    }).then((resJson) => {
        //console.log(resJson);
        resJson.forEach((eventGroup) => {
            // Create the new event group with empty events array first
            const newEventGroup = {...(new _EventGroup({
                ...eventGroup,
                ...{
                    events: []
                }
            }))};
            
            // Push to the array instead of reassignment
            eventGroupState.push(newEventGroup);

            // The new event group is now at the end of the array
            const newIndex = eventGroupState.length - 1;
            
            // Add events to the newly added event group
            eventGroup.events.forEach(event => {
                eventGroupState[newIndex] = insertEvent(eventGroupState[newIndex], event);
                
                // Check if the event has speakers assigned
                if (event.speakers && Array.isArray(event.speakers) && event.speakers.length > 0) {
                    // For each speaker in the event
                    event.speakers.forEach(eventSpeaker => {
                        // Check if this speaker already exists in the speaker store
                        const speakerExists = speakerState.some(speaker => speaker.id === eventSpeaker.id);
                        
                        // If the speaker doesn't exist, add it to the speaker store
                        if (!speakerExists && eventSpeaker.id) {
                            speakerState.push({...eventSpeaker});
                        }
                    });
                }
            });
        });

        toast.push(m.automatic_save_event_groups_restored(), {
            classes: ["toast-success"]
        });

        hideModal();

    }).catch(error => {
        //console.log(error, error.message);
        toast.push(m.automatic_save_event_groups_restore_failed({ message: error.message }), {
            classes: ["toast-danger"]
        });
        console.log(error, error.message);
    }).finally(() => {
        loading = false;
    });
}
</script>

<div 
    transition:fade={{duration: 150}} 
    class="modal {modalState.selected === 'autoSave' ? 'show d-block' : ''}" 
    role="dialog" 
    tabindex="-1"
    aria-labelledby="autoSaveTitle"
    aria-describedby="autoSaveDescription">
    <div class="modal-dialog modal-xl modal-dialog-centered" role="document">
        <div class="modal-content">
            <div class="modal-header">
                <h4 class="modal-title text-capitalize" id="autoSaveTitle">{m.automatic_save_title()}</h4>
                <button type="button" 
                        class="btn-close" 
                        aria-label={m.automatic_save_close_label()} 
                        onclick={hideModal}>
                </button>
            </div>
            <div class="modal-body">
                <div class="mx-3 my-1">
                    <button type="button" 
                            id="reloadRecordsBtn"
                            class="btn btn-info text-white" 
                            aria-label={m.automatic_save_reload_records_label()} 
                            onclick={() => refreshRecords()} 
                            disabled={loading}>
                        <i class="bi bi-arrow-clockwise" aria-hidden="true"></i> {m.automatic_save_reload_records_button()}
                    </button>
                </div>
                <div class="m-3 rounded-lg shadow border border-secondary">
                    <div class="table-responsive" role="region" aria-label={m.automatic_save_records_region_label()}>
                        <table class="table table-hover table-light" aria-label={m.automatic_save_records_table_label()}>
                            <thead class="table-light">
                                <tr>
                                    <th>{m.automatic_save_table_project_name()}</th>
                                    <th>{m.automatic_save_table_created_on()}</th>
                                    <th>{m.automatic_save_table_event_groups()}</th>
                                    <th>{m.automatic_save_table_events()}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {#await gettingRecords}
                                <tr>
                                    <td colspan="4" style="text-align: center;">
                                        <div style="margin: 5% 35%">
                                            <BarLoader size="160" color="#1eb4b2" unit="px" duration="3s"></BarLoader>
                                        </div>
                                    </td>
                                </tr>
                                {:then}
                                {#each records as record (record.id)}
                                <tr transition:fade={{duration: 100}} ondblclick={() => restoreEventGroups()} onclick={() => selectRecord(record)} class={selectedRecord && record.id === selectedRecord.id ? 'table-warning' : ''} >
                                    <td class="text-truncate" title={record.name}>{record.name}
                                    </td>
                                    <td class="text-truncate" title={record.createdOn.toDate()}>{record.createdOn.toDate()}</td>
                                    <td class="text-truncate" title={record.eventGroups}>{record.eventGroups}</td>
                                    <td class="text-truncate" title={record.events}>{record.events}</td>
                                </tr>
                                {:else}
                                <tr>
                                    <td>{m.automatic_save_no_records_prompt()}</td>
                                </tr>
                                {/each}
                                {/await}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
            <div class="modal-footer">
                {#if loading}
                <p class="text-muted small" role="status" aria-live="polite">{m.automatic_save_importing_event_groups()}</p>
                <Circle size="30" color="#1eb4b2" unit="px" duration="1s"></Circle>
                {/if}
                <button type="button" 
                        id="restoreBtn"
                        class="btn btn-primary" 
                        disabled={!selectedRecord || loading} 
                        onclick={restoreEventGroups}
                        aria-label={m.automatic_save_restore_aria_label()}>
                    <i class="bi bi-download" aria-hidden="true"></i> {m.automatic_save_restore_button()}
                </button>
            </div>
        </div>
    </div>
</div>

<style lang="css">
table {
    display: flex;
    flex-flow: column;
    width: 100%;
    height: 60vh;
}

thead {
    flex: 0 0 auto;
}

tbody {
    flex: 1 1 auto;
    display: block;
    overflow-y: auto;
    overflow-x: hidden;
}

tr {
    width: 100%;
    display: table;
    table-layout: fixed;
}
</style>
